import { useEffect, useState } from "react"
import type { ImagemProduto } from "@/api"
import { Box, Fade, IconButton, Stack } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { urlImagemProduto } from "@/lib/imagem"
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'

const INTERVALO_MS = 4000

interface CarrosselImagensProps {
    imagens: ImagemProduto[]
    alt: string
}

export default function CarrosselImagens({ imagens, alt }: CarrosselImagensProps): JSX.Element {
    const [indice, setIndice] = useState(0)
    const total = imagens.length

    useEffect(() => {
        if (total <= 1) return

        const timer = setInterval(() => setIndice((i) => (i + 1) % total), INTERVALO_MS)
        return () => clearInterval(timer)
    }, [indice, total])

    useEffect(() => {
        setIndice(0)
    }, [imagens])

    if (total === 0) {
        return (
            <Stack
                spacing={1}
                sx={{
                    width: '100%',
                    aspectRatio: '4/3',
                    borderRadius: 2,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'action.hover',
                }}
            >
                <ImageNotSupportedOutlinedIcon sx={{ fontSize: 64, opacity: 0.24 }} />
            </Stack>
        )
    }

    const imagemAtual = imagens[indice] ?? imagens[0]
    const url = urlImagemProduto(imagemAtual, { largura: 640, altura: 480, modo: 'cover' })
    const irPara = (delta: number): void => setIndice((i) => (i + delta + total) % total)

    return (
        <Box
            sx={{
                width: '100%',
                aspectRatio: '4/3',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: 'action.hover',
            }}
        >
            <Fade in key={imagemAtual?.id ?? indice} timeout={400}>
                <Box
                    component="img"
                    src={url}
                    alt={alt}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            </Fade>

            {total > 1 && (
                <>
                    <IconButton
                        onClick={() => irPara(-1)}
                        aria-label="Imagem anterior"
                        sx={{
                            left: 8,
                            top: '50%',
                            position: 'absolute',
                            transform: 'translateY(-50%)',
                            color: 'text.primary',
                            backgroundColor: (t) => alpha(t.palette.background.paper, 0.85),
                            '&:hover': { backgroundColor: 'background.paper' },
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    <IconButton
                        onClick={() => irPara(1)}
                        aria-label="Próxima imagem"
                        sx={{
                            right: 8,
                            top: '50%',
                            position: 'absolute',
                            transform: 'translateY(-50%)',
                            color: 'text.primary',
                            backgroundColor: (t) => alpha(t.palette.background.paper, 0.85),
                            '&:hover': { backgroundColor: 'background.paper' },
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ left: 0, right: 0, bottom: 8, position: 'absolute', justifyContent: 'center' }}
                    >
                        {imagens.map((img, i) => (
                            <Box
                                key={img.id ?? i}
                                onClick={() => setIndice(i)}
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    backgroundColor: (t) => i === indice ? t.palette.primary.main : alpha(t.palette.common.white, 0.6),
                                    boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
                                }}
                            />
                        ))}
                    </Stack>
                </>
            )}
        </Box>
    )
}